'use client';

/**
 * useLivePlaceOrder — the order the guest is currently waiting on, for the shell.
 *
 * WHY IT EXISTS. `resolvePlaceState` has always known how to say "Order #A24 ·
 * being cooked" and "ready to pay", and the action bar renders both. Nothing
 * ever supplied it: `placeSituation` passed the branch, the session and the
 * mode, and never an order. So the two most important states the shell can be
 * in were unreachable — a guest placed an order and the bar went straight back
 * to empty, as if nothing had happened. That is the exact "not in sync"
 * complaint, at the one moment it matters most.
 *
 * WHY IT READS localStorage RATHER THAN THE ISLAND. The ordering island already
 * holds order records (`useCustomerOrderRecords`), but it is mounted below the
 * chrome and only for some fulfilment modes — routing through it would repeat
 * the mistake PlaceProvider exists to correct, and would leave the shell blind
 * whenever the island happens not to be mounted. `listGuestOrders()` is the
 * durable, mode-independent record of "orders this device placed", written at
 * checkout, so the shell can read it directly and stay correct across reloads
 * and mode changes.
 *
 * WHY IT POLLS. The stored record carries no status — only id, token and
 * number — so the status has to come from the server. Polling rather than
 * realtime because this is one row, on a public surface, for an unauthenticated
 * guest: a Supabase realtime channel per menu visitor is a far larger bill and
 * a far larger attack surface than one small request a minute. It stops
 * completely the moment the order reaches a terminal state, and never starts at
 * all for the overwhelmingly common case of a guest with no orders here.
 *
 * @see lib/customer/place-state.ts — what it feeds
 * @see components/place/PlaceActionBar.tsx — what renders the result
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { listGuestOrders, isUnpaidPaymentStatus, type GuestOrderRecord } from '@/lib/customer/guest-orders';
import { placeOrderTrackPath } from '@/lib/routing/place-href';
import { isOrderOver } from '@/lib/order-lifecycle';

/** Exactly the shape `resolvePlaceState` consumes, and nothing more. */
export type LivePlaceOrder = {
  /** The short number the guest reads aloud to staff — never the UUID. */
  code: string;
  status: string;
  paid: boolean;
  /**
   * The full-page track URL for this order, token included (doc 70 P6).
   *
   * The live-order bar normally opens the orders SHEET, which the ordering
   * island registers. But the island is not always mounted — a browse-only
   * branch, or a mode that renders a different flow — and when it is absent
   * `place.actions.openOrders` is undefined, so the bar rendered a primary
   * full-width button wired to `onClick={undefined}`: a dead control announcing
   * the guest's own order. This is the fallback destination, so the bar can be a
   * link when it cannot be a button.
   */
  trackHref: string | null;
};

/** Quiet enough not to be a drain, live enough that "ready" reaches the guest
 *  while they are still looking at the screen. */
const POLL_MS = 25_000;

export function useLivePlaceOrder(menuSlug: string | null | undefined): LivePlaceOrder | null {
  const [order, setOrder] = useState<LivePlaceOrder | null>(null);
  const [record, setRecord] = useState<GuestOrderRecord | null>(null);
  /** Set once this device's newest order is finished, so polling stops for good
   *  rather than idling forever on a menu left open on a table. */
  const settledRef = useRef(false);

  // Which order to watch. Re-read on the custom event `saveGuestOrder` fires,
  // so placing an order lights the bar up without a reload.
  useEffect(() => {
    if (!menuSlug) return;

    const pick = () => {
      // listGuestOrders() is newest-first and already TTL-pruned. Restrict to
      // THIS menu: a guest with an order at another restaurant must not see it
      // announced on this one.
      const next = listGuestOrders().find((entry) => entry.menuSlug === menuSlug) ?? null;
      setRecord((prev) => {
        if (prev?.id === next?.id) return prev;
        // A different order means a fresh subject — let it poll again.
        settledRef.current = false;
        return next;
      });
    };

    pick();
    window.addEventListener('mnrq:guest-orders-changed', pick);
    return () => window.removeEventListener('mnrq:guest-orders-changed', pick);
  }, [menuSlug]);

  const fetchStatus = useCallback(async (target: GuestOrderRecord, signal: AbortSignal) => {
    try {
      const res = await fetch(`/api/public/orders/${target.id}/status`, {
        headers: { Authorization: `Bearer ${target.token}` },
        signal,
      });
      // 401/404 mean the token expired or the order is gone. Not an error worth
      // showing a guest who is reading a menu — just stop claiming they have an
      // order in flight, and stop asking.
      if (!res.ok) {
        settledRef.current = true;
        setOrder(null);
        return;
      }
      const body = await res.json();
      const found = body?.order;
      if (!found?.status) {
        settledRef.current = true;
        setOrder(null);
        return;
      }

      const status = String(found.status);
      if (isOrderOver(status)) {
        // Delivered, served, cancelled — the shell has nothing left to say, and
        // an "Order #A24 · served" bar sitting over the menu forever is the
        // stale chrome this redesign removed everywhere else.
        settledRef.current = true;
        setOrder(null);
        return;
      }

      setOrder({
        code: String(found.orderNumber ?? target.orderNumber ?? ''),
        status,
        paid: !isUnpaidPaymentStatus(found.paymentStatus),
        // Built from the SAVED record, which is the only place the access token
        // lives — the status response deliberately does not echo it back.
        trackHref: target.menuSlug
          ? placeOrderTrackPath({
              business: target.menuSlug,
              orderId: target.id,
              accessToken: target.token,
              experience: 'v2',
            })
          : null,
      });
    } catch (err) {
      // AbortError is our own cleanup. Anything else is a transient network
      // problem: leave the last known state on screen and try again next tick
      // rather than blanking the bar under the guest.
      if (err instanceof DOMException && err.name === 'AbortError') return;
    }
  }, []);

  useEffect(() => {
    if (!record) {
      setOrder(null);
      return;
    }

    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = () => {
      if (settledRef.current || controller.signal.aborted) return;
      void fetchStatus(record, controller.signal).finally(() => {
        if (settledRef.current || controller.signal.aborted) return;
        timer = setTimeout(tick, POLL_MS);
      });
    };

    tick();

    // A guest who backgrounds the tab while waiting for food comes back wanting
    // the current answer, not one from ten minutes ago.
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || settledRef.current) return;
      void fetchStatus(record, controller.signal);
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      controller.abort();
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [record, fetchStatus]);

  return order;
}
